import { SETTINGS_REGISTRY } from './settingsRegistry';
import type { SettingCoordinate, SettingCoordinateValue } from './settingsLayersSlice';
import type { DeviceType, LearningMode, SettingDefinition } from './settingTypes';

/**
 * The account store's shapes (`/user-settings`) and the translation between
 * them and the model's coordinates. Pure: the sync (`accountSettingsSync`)
 * does the reading and writing.
 */

interface AccountValueColumns {
  value_boolean?: boolean | null;
  value_number?: number | null;
  value_text?: string | null;
}

/** An account-wide, per-mode or per-device value. */
export interface AccountSettingRow extends AccountValueColumns {
  setting_key: string;
  learning_mode: 'all' | LearningMode;
  device_type: 'all' | DeviceType;
}

/** A value for one piece of practice. */
export interface AccountInstanceSettingRow extends AccountValueColumns {
  setting_key: string;
  learning_mode: LearningMode;
  instance_key: string;
}

/** What GET returns (every value the player set) and PUT takes. */
export interface AccountSettingsPayload {
  values: AccountSettingRow[];
  instance_values: AccountInstanceSettingRow[];
}

export interface AccountSettingEntry extends SettingCoordinateValue {
  definition: SettingDefinition<unknown>;
}

const DEFINITIONS: readonly SettingDefinition<unknown>[] = Object.values(SETTINGS_REGISTRY).map(
  (definition) => definition as SettingDefinition<unknown>,
);

/** The settings the account store keeps. */
export const accountDefinitions = (): readonly SettingDefinition<unknown>[] =>
  DEFINITIONS.filter((definition) => definition.account !== undefined);

const definitionForAccountKey = (key: string): SettingDefinition<unknown> | undefined =>
  accountDefinitions().find((definition) => definition.account?.key === key);

const definitionForRegistryKey = (key: string): SettingDefinition<unknown> | undefined =>
  accountDefinitions().find((definition) => definition.key === key);

/** Whether the account store keeps the value at this coordinate. */
export const isAccountCoordinate = (
  definition: SettingDefinition<unknown>,
  coordinate: SettingCoordinate,
): boolean => {
  const account = definition.account;
  if (!account) return false;
  // A per-piece setting is kept per piece only (its mode value is the device's).
  if (definition.scope === 'instance' && coordinate.layer !== 'instance') return false;
  if (coordinate.mode && account.modes && !account.modes.includes(coordinate.mode)) return false;
  if (definition.residence === 'device_type') return coordinate.deviceType !== undefined;
  return true;
};

const toValueColumns = (
  definition: SettingDefinition<unknown>,
  value: unknown,
): AccountValueColumns => {
  const parsed = definition.parse(value);
  const account = definition.account;
  switch (account?.kind) {
    case 'boolean':
      return { value_boolean: Boolean(parsed) };
    case 'number':
      return { value_number: Number(parsed) };
    case 'text':
      return { value_text: account.toText ? account.toText(parsed) : String(parsed) };
    default:
      throw new Error(`Setting ${definition.key} is not kept by the account store`);
  }
};

const fromValueColumns = (
  definition: SettingDefinition<unknown>,
  row: AccountValueColumns,
): unknown => {
  const account = definition.account;
  switch (account?.kind) {
    case 'boolean':
      return row.value_boolean ?? undefined;
    case 'number':
      return row.value_number ?? undefined;
    case 'text': {
      const text = row.value_text ?? undefined;
      if (text === undefined) return undefined;
      return account.fromText ? account.fromText(text) : text;
    }
    default:
      return undefined;
  }
};

/** The PUT body for player changes. Changes the account store does not keep are left out. */
export const toAccountSettingsPayload = (
  writes: readonly SettingCoordinateValue[],
): AccountSettingsPayload => {
  const kept = writes
    .map((write) => ({ ...write, definition: definitionForRegistryKey(write.coordinate.key) }))
    .filter(
      (write): write is AccountSettingEntry =>
        write.definition !== undefined && isAccountCoordinate(write.definition, write.coordinate),
    );
  return {
    values: kept
      .filter(({ coordinate }) => coordinate.layer !== 'instance')
      .map(({ definition, coordinate, value }) => ({
        setting_key: definition.account?.key ?? definition.key,
        learning_mode: coordinate.layer === 'global' ? 'all' : (coordinate.mode as LearningMode),
        device_type: coordinate.deviceType ?? 'all',
        ...toValueColumns(definition, value),
      })),
    instance_values: kept
      .filter(({ coordinate }) => coordinate.layer === 'instance')
      .map(({ definition, coordinate, value }) => ({
        setting_key: definition.account?.key ?? definition.key,
        learning_mode: coordinate.mode as LearningMode,
        instance_key: coordinate.instanceId as string,
        ...toValueColumns(definition, value),
      })),
  };
};

const rowEntry = (
  key: string,
  coordinateOf: (definition: SettingDefinition<unknown>) => SettingCoordinate,
  row: AccountValueColumns,
): AccountSettingEntry | undefined => {
  const definition = definitionForAccountKey(key);
  if (!definition) return undefined;
  const value = fromValueColumns(definition, row);
  if (value === undefined) return undefined;
  const coordinate = coordinateOf(definition);
  return isAccountCoordinate(definition, coordinate)
    ? { definition, coordinate, value: definition.parse(value) }
    : undefined;
};

/**
 * The model's coordinates and values for what GET returned. A value of a
 * setting this app does not know (a newer app's) is skipped, as is one this
 * app keeps elsewhere (the metronome settings, which it reads from user data).
 */
export const fromAccountSettingsPayload = (
  payload: Partial<AccountSettingsPayload> | undefined,
): AccountSettingEntry[] => {
  const values = (payload?.values ?? []).map((row) =>
    rowEntry(
      row.setting_key,
      (definition) => ({
        key: definition.key,
        layer: row.learning_mode === 'all' ? 'global' : 'mode',
        ...(row.learning_mode === 'all' ? {} : { mode: row.learning_mode }),
        ...(row.device_type === 'all' ? {} : { deviceType: row.device_type }),
      }),
      row,
    ),
  );
  const instanceValues = (payload?.instance_values ?? []).map((row) =>
    rowEntry(
      row.setting_key,
      (definition) => ({
        key: definition.key,
        layer: 'instance',
        mode: row.learning_mode,
        instanceId: row.instance_key,
      }),
      row,
    ),
  );
  return [...values, ...instanceValues].filter(
    (entry): entry is AccountSettingEntry => entry !== undefined,
  );
};
